import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { usePagePrefs } from "../lib/PagePrefs";

export const NCAA_BLUE = "#2563EB";
export const useNcaaTeams = () => useQuery({ queryKey: ["ncaa-teams"], queryFn: () => api.get("/ncaaw/teams").then((r) => r.data), staleTime: 6 * 60 * 60 * 1000 });

// NCAA teams switchboard: All Teams, then each conference (vertical tile) with its teams in 4 rows.
export function NcaaSurfBlock({ leadCls, ROW_H, before, after }) {
    const { getPref, setPref } = usePagePrefs();
    const { data: teams = [] } = useNcaaTeams();
    const sel = getPref("page_toggle", "ncaa_team") || null;
    const conf = getPref("page_toggle", "ncaa_conf") || null;
    const setSel = (t, c) => { setPref("page_toggle", "ncaa_team", t); setPref("page_toggle", "ncaa_conf", c); };
    const confs = [...new Set(teams.map((t) => t.conf))];
    const allOn = !sel && !conf;
    return (
        <div className="flex items-start gap-1 shrink-0 p-2 rounded-xl border bg-[#0A0B10]/80 backdrop-blur max-w-full overflow-x-auto" style={{ borderColor: `${NCAA_BLUE}40` }} data-testid="ncaa-team-surf-block">
            {before}
            <div className="flex items-start gap-1 shrink-0" data-testid="ncaa-team-surf-logos">
            {confs.map((c, ci) => {
                const list = teams.filter((t) => t.conf === c);
                const cOn = conf === c;
                const first = ci === 0;  // All Teams sits on top of the first conference (AHA) — no wasted column
                const cols = Math.max(3, Math.ceil(list.length / (first ? 2 : 3)));
                return (
                    <div key={c} className="grid gap-1 shrink-0" style={{ gridTemplateColumns: `repeat(${cols}, 42px)`, gridTemplateRows: `repeat(4, ${ROW_H}px)` }} data-testid={`ncaa-surf-conf-${c}`}>
                        {first && (
                            <button type="button" data-testid="ncaa-surf-all" onClick={() => setSel(null, null)} style={{ gridColumn: "1 / -1", ...(allOn ? { background: `${NCAA_BLUE}26`, borderColor: `${NCAA_BLUE}59`, color: "#fff" } : {}) }}
                                className={`rounded-lg border flex items-center justify-center text-[9px] font-black uppercase tracking-[0.12em] whitespace-nowrap transition-colors ${allOn ? "" : "bg-black/25 border-white/15 text-[#94A3B8] hover:text-white"}`}>All Teams</button>
                        )}
                        <button type="button" onClick={() => setSel(null, cOn ? null : c)} title={c} data-testid={`ncaa-surf-conf-btn-${c}`}
                            style={{ gridColumn: "1 / -1", ...(cOn ? { background: `${NCAA_BLUE}26`, borderColor: `${NCAA_BLUE}59` } : {}) }}
                            className={`rounded-lg border flex items-center justify-center text-[9px] font-black uppercase tracking-[0.12em] whitespace-nowrap transition-colors ${cOn ? "text-white" : "border-white/15 bg-black/25 text-white/70 hover:text-white"}`}>{c}</button>
                            {list.map((t) => {
                                const on = sel === t.id || cOn;
                                const col = t.primary_color || NCAA_BLUE;
                                return (
                                    <button key={t.id} type="button" onClick={() => setSel(sel === t.id ? null : t.id, null)} title={t.name} data-testid={`ncaa-team-${t.abbr}`}
                                        style={on ? { background: `${col}26`, borderColor: `${col}59` } : undefined}
                                        className={`inline-flex items-center justify-center w-[42px] h-full rounded-lg border transition-all ${on ? "" : "bg-black/25 border-white/15 hover:brightness-125"}`}>
                                        <img src={t.logo_url} alt={t.abbr} className="h-6 w-6 object-contain" loading="lazy" />
                                    </button>
                                );
                            })}
                    </div>
                );
            })}
            </div>
            {after}
        </div>
    );
}

// Mobile Teams sheet for NCAA: All Teams, then each conference with its logos.
export function MobileNcaaTeams() {
    const { getPref, setPref } = usePagePrefs();
    const { data: teams = [] } = useNcaaTeams();
    const sel = getPref("page_toggle", "ncaa_team") || null;
    const conf = getPref("page_toggle", "ncaa_conf") || null;
    const setSel = (t, c) => { setPref("page_toggle", "ncaa_team", t); setPref("page_toggle", "ncaa_conf", c); };
    const confs = [...new Set(teams.map((t) => t.conf))];
    const chip = (on, col = NCAA_BLUE) => (on ? { borderColor: `${col}99`, background: `${col}26`, color: "#fff" } : undefined);
    return (
        <div className="px-2.5 pb-2 space-y-2" data-testid="mobile-ncaa-teams">
            <button type="button" onClick={() => setSel(null, null)} style={chip(!sel && !conf)} data-testid="mobile-ncaa-all"
                className={`w-full h-8 rounded-md border text-[10px] font-black uppercase tracking-wide ${!sel && !conf ? "" : "border-white/10 text-[#94A3B8]"}`}>All Teams</button>
            {confs.map((c) => (
                <div key={c} className="flex items-stretch gap-1.5" data-testid={`mobile-ncaa-conf-${c}`}>
                    <button type="button" onClick={() => setSel(null, conf === c ? null : c)} style={chip(conf === c)}
                        className={`shrink-0 w-14 rounded-md border text-[9px] font-black uppercase tracking-wide ${conf === c ? "" : "border-white/10 text-[#94A3B8]"}`}>{c}</button>
                    <div className="flex-1 grid grid-cols-6 gap-1">
                        {teams.filter((t) => t.conf === c).map((t) => (
                            <button key={t.id} type="button" onClick={() => setSel(sel === t.id ? null : t.id, null)} title={t.name} data-testid={`mobile-ncaa-team-${t.abbr}`}
                                style={chip(sel === t.id || conf === c, t.primary_color || NCAA_BLUE)} className={`h-9 rounded-md border flex items-center justify-center ${sel === t.id || conf === c ? "" : "border-white/10"}`}>
                                <img src={t.logo_url} alt={t.abbr} className="h-6 w-6 object-contain" loading="lazy" />
                            </button>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
